import React, { Component } from 'react';
import {
  array,
  arrayOf,
  bool,
  func,
  number,
  object,
  shape,
  string,
} from 'prop-types';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, intlShape, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { createSlug, parse, stringify } from '../../../util/urlHelpers';
import {
  createResourceLocatorString,
  matchPathname,
  pathByRouteName,
} from '../../../util/routes';
import { propTypes } from '../../../util/types';
import {
  Avatar,
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  NamedLink,
  ModalMissingInformation,
} from '../../../components';

import SearchIcon from './SearchIcon';
import CloseIcon from './CloseIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';

import css from './Topbar.module.css';

/* Not used since always using mobile menu */
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;

  const currentParams = parse(search);
  if (currentParams[modalStateParam] === 'open') {
    // If the modal state is already open, do nothing
    return;
  }

  const searchString = `?${stringify({
    [modalStateParam]: 'open',
    ...currentParams,
  })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;

  const currentParams = parse(search);
  if (!currentParams[modalStateParam]) {
    // If the modal state is already closed, do nothing
    return;
  }

  const queryParams = pickBy(currentParams, (v, k) => k !== modalStateParam);
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      const testURL = new URL('http://my.marketplace.com' + href);
      const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
      if (matchedRoutes.length > 0) {
        const found = matchedRoutes[0];
        const to = { search: testURL.search, hash: testURL.hash };
        return {
          ...linkConfig,
          route: {
            name: found.route?.name,
            params: found.params,
            to,
          },
        };
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName
      ? cmsPageName
      : inboxPageName
      ? inboxPageName
      : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

class TopbarComponent extends Component {
  constructor(props) {
    super(props);

    // Initialize local state
    this.state = {
      isMobileMenuOpen: false,
      isMobileSearchOpen: false,
      searchPhrase: '', // New state for the search phrase
    };

    this.toggleMobileMenu = this.toggleMobileMenu.bind(this);
    this.toggleSearchMenu = this.toggleSearchMenu.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSelectSearch = this.handleSelectSearch.bind(this);
    this.handleSelectListing = this.handleSelectListing.bind(this);
    this.handleClearSearch = this.handleClearSearch.bind(this);
    this.handleLogout = this.handleLogout.bind(this);

    this.closeMenu = this.closeMenu.bind(this);
    this.closeSearchMenu = this.closeSearchMenu.bind(this);
  }

  // Toggle the mobile menu
  toggleMobileMenu(status) {
    this.setState(prevState => ({
      isMobileMenuOpen:
        status !== undefined ? status : !prevState.isMobileMenuOpen,
      isMobileSearchOpen: status ? false : prevState.isMobileSearchOpen, // Close search menu if opening mobile menu
    }));
  }

  // Toggle the search menu
  toggleSearchMenu(status) {
    this.setState(prevState => ({
      isMobileMenuOpen:
        status !== undefined ? status : !prevState.isMobileMenuOpen,
      shouldAutofocusSearch: true, // Set autofocus when opened via search icon
    }));
  }
  closeMenu() {
    this.setState({
      isMobileMenuOpen: false,
      shouldAutofocusSearch: false, // Reset autofocus when closing
    });
  }

  closeSearchMenu() {
    this.setState({ isMobileSearchOpen: false });
  }

  handleClearSearch() {
    const { history, routeConfiguration, currentSearchParams } = this.props;

    // Remove keywords from search params
    const { keywords, ...searchParamsWithoutKeywords } = currentSearchParams;

    // Clear the searchPhrase state
    this.setState({
      searchPhrase: '',
      shouldAutofocusSearch: false,
    });

    this.closeMenu();

    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        searchParamsWithoutKeywords
      )
    );
  }

  handleSubmit(values) {
    const { currentSearchParams } = this.props;
    const { history, config, routeConfiguration } = this.props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };

    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };

    // Update the searchPhrase state
    if (values?.keywords) {
      this.setState({ searchPhrase: values.keywords });
    }

    this.closeMenu();
    this.closeSearchMenu();

    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        searchParams
      )
    );
  }

  // Navigate to the SearchPage for a keyword or facet suggestion selected from
  // the keyword autocomplete dropdown. A keyword search keeps any active
  // filters; choosing a facet (category/brand/...) drops a stale keyword so the
  // results aren't over-constrained.
  handleSelectSearch(selectedParams) {
    const { history, routeConfiguration, currentSearchParams } = this.props;
    const isKeyword = Object.prototype.hasOwnProperty.call(
      selectedParams,
      'keywords'
    );
    const base = { ...currentSearchParams };
    if (!isKeyword) {
      delete base.keywords;
    }
    const searchParams = { ...base, ...selectedParams };

    if (isKeyword) {
      this.setState({ searchPhrase: selectedParams.keywords });
    }

    this.closeMenu();
    this.closeSearchMenu();

    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        searchParams
      )
    );
  }

  // Navigate straight to a listing when a live listing suggestion is selected.
  handleSelectListing(listing) {
    const { history, routeConfiguration } = this.props;
    if (!listing?.id) {
      return;
    }
    this.closeMenu();
    this.closeSearchMenu();
    history.push(
      createResourceLocatorString(
        'ListingPage',
        routeConfiguration,
        { id: listing.id, slug: listing.slug || createSlug(listing.title || '') },
        {}
      )
    );
  }

  handleLogout() {
    const { onLogout, history, routeConfiguration } = this.props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }
    });
  }

  render() {
    const {
      className,
      rootClassName,
      desktopClassName,
      mobileRootClassName,
      mobileClassName,
      isAuthenticated,
      isLoggedInAs,
      authScopes,
      authInProgress,
      currentUser,
      onLogin,
      onSignup,
      loginError,
      signupError,
      currentUserHasListings,
      currentUserHasOrders,
      currentPage,
      notificationCount,
      listingsCount,
      favoritesCount,
      savedSearchesCount,
      intl,
      location,
      onManageDisableScrolling,
      onResendVerificationEmail,
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      showGenericError,
      config,
      routeConfiguration,
      isScrolled,
      suggestionListings,
      suggestionsInProgress,
      onFetchSearchSuggestions,
      onClearSearchSuggestions,
    } = this.props;

    const { keywords, address, origin, bounds } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    // Props for the keyword autocomplete dropdown, bundled to avoid threading
    // many individual props through TopbarMobileMenu and the search forms.
    const savedSearches =
      currentUser?.attributes?.profile?.privateData?.savedSearches || [];
    const searchSuggestionProps = {
      suggestionListings,
      suggestionsInProgress,
      savedSearches,
      onFetchSearchSuggestions,
      onClearSearchSuggestions,
      onSelectListing: this.handleSelectListing,
      onSelectSearch: this.handleSelectSearch,
    };

    // Custom links are sorted so that group="primary" are always at the beginning of the list.
    const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
    const customLinks = getResolvedCustomLinks(
      sortedCustomLinks,
      routeConfiguration
    );
    const categories = config.categoryConfiguration?.categories;
    const resolvedCurrentPage =
      currentPage || getResolvedCurrentPage(location, routeConfiguration);

    /* Not used since always using mobile menu */
    const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
    const isMobileLayout = hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

    const { isMobileMenuOpen, isMobileSearchOpen } = this.state;

    const topbarSearcInitialValues = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords };
      }

      // Only render current search if full place object is available in the URL params
      const locationFieldsPresent = isOriginInUse(config)
        ? address && origin && bounds
        : address && bounds;
      return {
        location: locationFieldsPresent
          ? {
              search: address,
              selectedPlace: { address, origin, bounds },
            }
          : null,
      };
    };

    const initialSearchFormValues = topbarSearcInitialValues();

    const mobileMenu = (
      <TopbarMobileMenu
        isAuthenticated={isAuthenticated}
        currentUserHasListings={currentUserHasListings}
        currentUser={currentUser}
        onLogout={this.handleLogout}
        onLogin={onLogin}
        onSignup={onSignup}
        loginError={loginError}
        signupError={signupError}
        authInProgress={authInProgress}
        notificationCount={notificationCount}
        listingsCount={listingsCount}
        favoritesCount={favoritesCount}
        savedSearchesCount={savedSearchesCount}
        currentPage={resolvedCurrentPage}
        categories={categories}
        customLinks={customLinks}
        isOpen={isMobileMenuOpen}
        closeMenu={this.closeMenu} // Pass closeMenu to TopbarMobileMenu
        onSubmit={this.handleSubmit}
        initialValues={initialSearchFormValues}
        isMobile
        appConfig={config}
        shouldAutofocusSearch={this.state.shouldAutofocusSearch}
        searchSuggestionProps={searchSuggestionProps}
      />
    );

    const classes = classNames(rootClassName || css.root, className);

    const LoginUser = () => {
      return isAuthenticated && currentUser ? (
        <NamedLink
          className={classNames(css.navigationLink, css.avatarContainer)}
          name="NotificationsPage"
        >
          <Avatar
            className={css.avatar}
            user={currentUser}
            disableProfileLink
          />
          {notificationCount > 0 && (
            <span className={css.notificationDot}>{notificationCount}</span>
          )}
        </NamedLink>
      ) : (
        <NamedLink className={classNames(css.navigationLink)} name="LoginPage">
          <Button rootClassName={css.menu}>
            <FormattedMessage id="Topbar.login" />
          </Button>
        </NamedLink>
      );
    };

    const SearchButton = initialSearchFormValues => {
      if (initialSearchFormValues.keywords !== undefined) {
        return (
          <Button
            rootClassName={css.searchMenuActive}
            onClick={() => this.toggleSearchMenu()}
            title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
          >
            <SearchIcon className={css.searchMenuIconActive} />
          </Button>
        );
      } else {
        return (
          <Button
            rootClassName={css.searchMenu}
            onClick={() => this.toggleSearchMenu()}
            title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
          >
            {isMobileSearchOpen ? (
              <CloseIcon className={css.closeMenuIcon} />
            ) : (
              <SearchIcon className={css.searchMenuIcon} />
            )}
          </Button>
        );
      }
    };

    return (
      <div className={classes}>
        <LimitedAccessBanner
          isAuthenticated={isAuthenticated}
          isLoggedInAs={isLoggedInAs}
          authScopes={authScopes}
          currentUser={currentUser}
          onLogout={this.handleLogout}
          currentPage={resolvedCurrentPage}
        />
        <div
          className={classNames(
            mobileRootClassName || css.container,
            mobileClassName
          )}
        >
          <div className={css.containerLeft}>
            <Button
              rootClassName={isMobileMenuOpen ? css.menuIcon : css.menuText}
              onClick={() => this.toggleMobileMenu()}
              title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
            >
              {isMobileMenuOpen ? (
                <CloseIcon className={css.closeMenuIcon} />
              ) : (
                'Menu'
              )}
            </Button>
            {!isMobileMenuOpen && SearchButton(initialSearchFormValues)}
          </div>
          <div
            className={css.containerMiddle}
            style={{
              opacity:
                resolvedCurrentPage !== 'LandingPage' ||
                isScrolled ||
                isMobileMenuOpen
                  ? 1
                  : 0,
              transition: 'opacity 200ms ease',
              pointerEvents:
                resolvedCurrentPage !== 'LandingPage' ||
                isScrolled ||
                isMobileMenuOpen
                  ? 'auto'
                  : 'none',
            }}
          >
            <LinkedLogo
              layout={'desktop'}
              className={css.logoDesktop}
              alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
            <LinkedLogo
              layout={'mobile'}
              className={css.logoMobile}
              alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
          </div>

          <div className={css.containerRight}>
            <NamedLink className={css.navigationLink} name="NewListingPage">
              <Button
                rootClassName={css.menu}
                className={css.createListingButton}
              >
                <FormattedMessage id="Topbar.newListing" />
              </Button>
            </NamedLink>

            <LoginUser />
          </div>
        </div>
        {/* Toggle Mobile Menu on click */}
        {isMobileMenuOpen && <>{authInProgress ? null : mobileMenu}</>}
        {isMobileSearchOpen && (
          <div className={css.searchWrapper}>
            <div className={css.searchContainer}>
              <TopbarSearchForm
                onSubmit={this.handleSubmit}
                initialValues={initialSearchFormValues}
                isMobile
                appConfig={config}
                searchSuggestionProps={searchSuggestionProps}
              />
              {/* <p className={css.mobileHelp}>
              <FormattedMessage id="Topbar.mobileSearchHelp" />
            </p> */}
            </div>
          </div>
        )}
        <ModalMissingInformation
          id="MissingInformationReminder"
          containerClassName={css.missingInformationModal}
          currentUser={currentUser}
          currentUserHasListings={currentUserHasListings}
          currentUserHasOrders={currentUserHasOrders}
          location={location}
          onManageDisableScrolling={onManageDisableScrolling}
          onResendVerificationEmail={onResendVerificationEmail}
          sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          sendVerificationEmailError={sendVerificationEmailError}
        />
        <GenericError show={showGenericError} />
      </div>
    );
  }
}

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  notificationCount: 0,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
  authScopes: [],
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  isAuthenticated: bool.isRequired,
  isLoggedInAs: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  onLogin: func.isRequired,
  onSignup: func.isRequired,
  loginError: propTypes.error,
  signupError: propTypes.error,
  onManageDisableScrolling: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
